import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { IntroContent } from 'src/common/constants';
import Empty from 'src/components/Empty';
import ContentBlock from 'src/components/ContentBlock';
import { ServiceRequestDashboardTable } from 'src/pages/ServiceRequests/components/ServiceRequestDashboardTable';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import ServiceRequestForm from 'src/components/ServiceRequestForm/ServiceRequestForm';
import { ServiceRequestDashboardActions } from 'src/pages/ServiceRequests/components/ServiceRequestDashboardActions';
import ServiceRequestDashboardToolbar from 'src/pages/ServiceRequests/components/ServiceRequestDashboardToolbar/ServiceRequestDashboardToolbar';
import { AssignedStatusOverlay } from 'src/pages/ServiceRequests/components/Overlays/BulkStatus/AssignedStatusOverlay';
import { DeclinedStatusOverlay } from 'src/pages/ServiceRequests/components/Overlays/BulkStatus/DeclinedStatusOverlay';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVAndPDFParserOverlay/DownloadCSVAndPDFParserOverlay';
import { DetailedSuite } from 'src/components/ContactForm/types';
import 'src/pages/ServiceRequests/ServiceRequestDashBoardTable.scss';
import { UseSuiteServiceRequestsOrchestratorReturn } from './CustomHooks/SuiteServiceRequests/useSuiteServiceRequestsOrchestrator';

export interface SuiteServiceRequestsProps {
  suite: DetailedSuite;
  orchestrator: UseSuiteServiceRequestsOrchestratorReturn;
}

const isHomeOwnerWarrantyBlocked = (
  warrantyStartDate: string,
  roleName: string
): boolean => {
  if (roleName !== 'Home Owner') {
    return false;
  }

  if (!warrantyStartDate) {
    return false;
  }

  const today: Date = new Date();
  today.setHours(0, 0, 0, 0);

  const warrantyStart: Date = new Date(warrantyStartDate);
  warrantyStart.setHours(0, 0, 0, 0);

  return warrantyStart > today;
};

const WarrantyDisabledMessage = ({
  warrantyStartDate,
}: {
  warrantyStartDate: string;
}) => {
  const [year, month, day] = warrantyStartDate.split('-');
  const formatted: string = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10)
  ).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div
      style={{
        color: '#f56565',
        fontSize: '14px',
        fontWeight: 500,
        textAlign: 'left',
        marginTop: '8px',
        maxWidth: '600px',
      }}
    >
      Homeowner Service Requests cannot be submitted yet. Your warranty
      coverage begins on <strong>{formatted}</strong>. Please wait until this
      date or contact your property developer for assistance.
    </div>
  );
};

const SuiteServiceRequests = ({
  suite,
  orchestrator,
}: SuiteServiceRequestsProps) => {
  const user: any = useSelector(selectAuthenticatedUser);
  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const [isServiceRequestFormOpen, setIsServiceRequestFormOpen] =
    useState<boolean>(false);

  const isCreateRequestDisabled: boolean = isHomeOwnerWarrantyBlocked(
    suite.assetWarrantyDefaultStartDate,
    roleName
  );

  // Plumbed unchanged into the legacy ServiceRequestForm so the existing create-SR flow keeps working — see ServiceRequestFormSteps for fields read.
  const serviceRequestConfig = useMemo(
    () => ({
      property_id: suite.propertyId,
      propertyId: suite.propertyId,
      suiteId: suite.id,
      suite_id: suite.id,
      suiteFloorPLan:
        suite.floorPlanURL && suite.floorPlanURL.length > 0
          ? suite.floorPlanURL
          : '',
      asset_warranty_star_date: suite.assetWarrantyDefaultStartDate,
    }),
    [
      suite.propertyId,
      suite.id,
      suite.floorPlanURL,
      suite.assetWarrantyDefaultStartDate,
    ]
  );

  const isInitialLoading: boolean =
    orchestrator.isLoading && orchestrator.serviceRequests.length === 0;

  const showEmpty: boolean =
    !orchestrator.isLoading &&
    orchestrator.visibleServiceRequests.length === 0;

  const canCreateServiceRequest: boolean =
    roleName !== 'Vendor' &&
    roleName !== 'Service Provider' &&
    roleName !== 'Realtor';

  return (
    <div>
      {isServiceRequestFormOpen && (
        <ServiceRequestForm
          propertyDisabled={true}
          serviceRequest={serviceRequestConfig}
          from="Suite"
          onClose={() => setIsServiceRequestFormOpen(false)}
        />
      )}

      <DownloadCSVAndPDFParserOverlay
        isOpen={orchestrator.isDownloadOverlayOpen}
        onClose={orchestrator.closeDownloadOverlay}
        onDownloadCSV={orchestrator.onDownloadCSV}
        isCSVLoading={orchestrator.isCSVLoading}
        onDownloadPDF={orchestrator.onDownloadPDF}
        isPDFLoading={orchestrator.isPDFLoading}
      />

      <AssignedStatusOverlay
        showAssignedStatusOverlay={orchestrator.isAssignOverlayOpen}
        setShowAssignedStatusOverlay={(show: boolean) => {
          if (!show) {
            orchestrator.closeAssignOverlay();
          }
        }}
        serviceRequests={orchestrator.selectedServiceRequests}
        fetchDashboard={() => void orchestrator.refetch()}
      />

      <DeclinedStatusOverlay
        showDeclinedStatusOverlay={orchestrator.isDeclineOverlayOpen}
        setShowDeclinedStatusOverlay={(show: boolean) => {
          if (!show) {
            orchestrator.closeDeclineOverlay();
          }
        }}
        serviceRequestIds={orchestrator.selectedIds}
        fetchDashboard={() => void orchestrator.refetch()}
      />

      <ContentBlock
        content={
          isCreateRequestDisabled ? (
            <WarrantyDisabledMessage
              warrantyStartDate={suite.assetWarrantyDefaultStartDate}
            />
          ) : (
            IntroContent.serviceRequests
          )
        }
      />

      <ServiceRequestDashboardToolbar
        canCreateServiceRequest={canCreateServiceRequest}
        onCreateServiceRequest={() => setIsServiceRequestFormOpen(true)}
        createDisabled={isCreateRequestDisabled}
      >
        <ServiceRequestDashboardActions
          searchString={orchestrator.searchQuery}
          onChangeSearchString={orchestrator.setSearchQuery}
          showResolved={orchestrator.showResolvedOnly}
          onShowResolved={orchestrator.toggleShowResolved}
          onShowColumnSelectionDialog={orchestrator.openColumnSelectionDialog}
          onBulkStatusUpdate={(status: string) =>
            void orchestrator.bulkUpdateStatus(status)
          }
          disableGroup={orchestrator.selectedIds.length === 0}
          selectedCount={orchestrator.selectedIds.length}
          onDownload={orchestrator.openDownloadOverlay}
          hideGroupButton={true}
        />
      </ServiceRequestDashboardToolbar>

      {showEmpty ? (
        <Empty
          direction="ttb"
          headline="No service requests for this suite"
          strapline="Submitted service requests for this suite will be listed here."
          image={null}
          searchIsActive={orchestrator.searchQuery !== ''}
        />
      ) : (
        <ServiceRequestDashboardTable
          serviceRequests={orchestrator.visibleServiceRequests}
          selectedServiceRequests={orchestrator.selectedIds}
          onSelectServiceRequest={orchestrator.toggleSelection}
          onSelectGroup={orchestrator.toggleSelectionBatch}
          onSelectAll={orchestrator.toggleSelectAllVisible}
          allSelected={orchestrator.allVisibleSelected}
          onDeleteGroup={() => undefined}
          onRemoveFromGroup={() => undefined}
          navigateTo={orchestrator.navigateTo}
          navLabel={orchestrator.navLabel}
          persistKey={`${PAGINATION_KEYS.suiteServiceRequests}-${suite?.id}`}
          loading={isInitialLoading}
          showColumnSelectionDialog={orchestrator.showColumnSelectionDialog}
          onCloseColumnSelectionDialog={orchestrator.closeColumnSelectionDialog}
        />
      )}
    </div>
  );
};

export default SuiteServiceRequests;
